import { Suspense, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router";
import { ErrorBox, NotFound, PreviewBar, useNoIndex, useTitle } from "../components/Layout.js";
import { Drawing, Sketch } from "../components/lazy.js";
import { SaveFirst } from "../components/LoginForm.js";
import { JoinCard, PairSection } from "../components/Pairs.js";
import { typeName } from "../components/type-name.js";
import { TextsProvider } from "../components/texts.js";
import { Tone } from "../components/tone.js";
import { ApiError, api, type FeedbackValue, type LinkedResult } from "../data/api.js";
import { device, type SessionRecord } from "../data/device.js";
import { useIdentity } from "../data/identity.js";

// Odkaz na výsledek `/{jazyk}/r/{token}` (REQ-024): Skica v jazyce adresy. Majitel (vlastnický
// token v zařízení) vidí sdílení, zrušení odkazu a „Sedí to na mě?“, cizí návštěvník výzvu
// „Udělej si svůj“.

export function ResultPage() {
  const intl = useIntl();
  const lang = intl.locale;
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const identity = useIdentity();
  // Čerstvě dokončený test (REQ-033): jen pro první zobrazení, příznak se z historie hned smaže,
  // aby obnovení stránky obrazovku uložení neukázalo znovu.
  const [fresh] = useState(() => (location.state as { fresh?: boolean } | null)?.fresh === true);
  const [asked, setAsked] = useState(false);
  useEffect(() => {
    if ((location.state as { fresh?: boolean } | null)?.fresh === true) void navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);
  const [data, setData] = useState<LinkedResult | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "missing" | "error">("loading");
  const [rec, setRec] = useState<SessionRecord | undefined>(undefined);
  const [feedback, setFeedback] = useState<Record<string, FeedbackValue>>({});
  const [revoked, setRevoked] = useState(false);
  useNoIndex();
  useTitle(data === null ? undefined : typeName(data.texts, data.payload.type, data.locale));

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const mine = await device.byResultToken(token);
      setRec(mine);
      const r = await api.result(token, lang, mine?.token);
      setData(r);
      setFeedback(Object.fromEntries(r.feedback.filter((f) => f.scale_id !== null && f.section_key === null).map((f) => [f.scale_id, f.value])));
      setStatus("ok");
    } catch (e) {
      setStatus(e instanceof ApiError && e.status === 404 ? "missing" : "error");
    }
  }, [token, lang]);

  useEffect(() => {
    void load();
  }, [load]);

  if (status === "missing") return <NotFound />;
  if (status === "error") return <ErrorBox onRetry={() => void load()} />;
  if (data === null) return null;
  const owner = data.owner && rec !== undefined;
  const preview = rec?.origin === "preview";
  if (fresh && !asked && owner && !preview && identity.kind === "anon" && rec?.saved !== true) return <SaveFirst onSkip={() => setAsked(true)} />;

  async function revoke() {
    if (rec === undefined) return;
    const { result_token: fresh } = await api.revoke(token, rec.token);
    await device.saveSession({ ...rec, resultToken: fresh });
    setRevoked(true);
    navigate(`/${lang}/r/${fresh}`, { replace: true });
  }

  return (
    <TextsProvider texts={data.texts} locale={data.locale} preview={preview}>
      {preview ? <PreviewBar /> : null}
      <Tone slug={data.view.slug}>
        <Suspense fallback={<Drawing />}>
          <Sketch
            payload={data.payload}
            view={data.view}
            sessionId={rec?.id ?? null}
            pictureUrl={data.picture_url ?? null}
            portrait={owner && !preview && rec !== undefined && data.portrait !== undefined ? { offer: data.portrait, owner: { kind: "session", id: rec.id, token: rec.token }, page: `/${lang}/s/${rec.id}/portrait` } : null}
            portraitSource={owner && rec !== undefined ? { kind: "link", token, owner: rec.token } : null}
            top={owner && !preview && rec !== undefined ? <JoinCard session={{ id: rec.id, token: rec.token }} /> : null}
            pairs={owner && !preview && rec !== undefined ? <PairSection owner={{ kind: "device", session: { id: rec.id, token: rec.token } }} /> : null}
            owner={owner}
            shareUrl={`${window.location.origin}/${lang}/r/${token}`}
            revoked={revoked}
            onRevoke={owner ? revoke : null}
            feedback={(d) =>
              owner
                ? {
                    value: feedback[d] ?? null,
                    onChange: (v) => {
                      setFeedback((f) => ({ ...f, [d]: v }));
                      void api.feedback(token, rec.token, { scale_id: d, section_key: null, value: v }).catch(() => setFeedback((f) => ({ ...f, [d]: feedback[d] ?? v })));
                    },
                  }
                : null
            }
          />
        </Suspense>
      </Tone>
    </TextsProvider>
  );
}
